/**
 * StagingStep
 *
 * The "Magic Moment" - photo upload, structure selection, style selection, and AI staging.
 * World-class design with immersive visual experience.
 *
 * Design Philosophy:
 * - Dramatic visual hierarchy
 * - Cinematic image presentations
 * - Refined micro-interactions
 * - Editorial-grade typography
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  Upload,
  Image as ImageIcon,
  ChevronRight,
  X,
  Check,
  Sofa,
  Bed,
  ChefHat,
  UtensilsCrossed,
  Briefcase,
  Bath,
} from 'lucide-react';
import { cn } from '../../../lib/cn';
import {
  OnboardingLayout,
  OnboardingStepContainer,
  OnboardingHeading,
  OnboardingButton,
} from '../OnboardingLayout';
import {
  stagingInProgressAtom,
  setStagingResultAtom,
} from '../../../state/onboardingAtoms';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { uploadToR2, generateOriginalImagePath, getR2SignedUrl } from '../../../lib/r2Storage';
import { stageImage, getUserFriendlyStagingError } from '../../../lib/aiStaging';
import { getStructureSelection, type StructureSelection } from '../../../lib/structureTypes';
import type { OnboardingStepProps } from '../OnboardingRouter';

// Sample photos - using local images (structure type selected separately)
const SAMPLE_PHOTOS = [
  {
    url: '/before-and-after/interior/living-spaces/living-room-modern-before.jpeg',
    label: 'Living Room',
  },
  {
    url: '/before-and-after/interior/bedrooms/primary-bedroom-scandinavian-before.jpeg',
    label: 'Bedroom',
  },
  {
    url: '/before-and-after/interior/kitchen/kitchen-island-modern-before.jpeg',
    label: 'Kitchen',
  },
];

// Structure type options for onboarding (simplified grid of common room types)
const STRUCTURE_OPTIONS = [
  { typeId: 'living_room', label: 'Living Room', icon: Sofa },
  { typeId: 'primary_bedroom', label: 'Bedroom', icon: Bed },
  { typeId: 'kitchen_island', label: 'Kitchen', icon: ChefHat },
  { typeId: 'dining_room', label: 'Dining Room', icon: UtensilsCrossed },
  { typeId: 'home_office', label: 'Home Office', icon: Briefcase },
  { typeId: 'full_bath', label: 'Bathroom', icon: Bath },
];

// Style options with local previews
const STYLE_OPTIONS = [
  { slug: 'modern', name: 'Modern', preview: '/images/styles-page/modern_staging_style.jpeg' },
  { slug: 'contemporary', name: 'Contemporary', preview: '/images/styles-page/contemporary_staging_style.jpeg' },
  { slug: 'minimalist', name: 'Minimalist', preview: '/images/styles-page/minimalist_staging_style.jpeg' },
  { slug: 'scandinavian', name: 'Scandinavian', preview: '/images/styles-page/scandinavian_staging_style.jpeg' },
  { slug: 'traditional', name: 'Traditional', preview: '/images/styles-page/traditional_staging_style.jpeg' },
  { slug: 'luxury', name: 'Luxury', preview: '/images/styles-page/luxury_staging_style.jpeg' },
];

// Processing messages for anticipation building
const PROCESSING_MESSAGES = [
  'Analyzing your space',
  'Selecting furniture',
  'Arranging layout',
  'Adding finishing touches',
  'Almost ready',
];

type Stage = 'upload' | 'structure' | 'style' | 'processing';

export function StagingStep({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const { user } = useAuth();
  const setStagingResult = useSetAtom(setStagingResultAtom);
  const setStagingInProgress = useSetAtom(stagingInProgressAtom);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<Stage>('upload');
  const [uploadedImage, setUploadedImage] = useState<{
    file?: File;
    url: string;
    isSample: boolean;
  } | null>(null);
  const [selectedStructure, setSelectedStructure] = useState<StructureSelection | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [processingMessage, setProcessingMessage] = useState(PROCESSING_MESSAGES[0]);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Rotate processing messages
  useEffect(() => {
    if (stage !== 'processing') return;

    let index = 0;
    const messageInterval = setInterval(() => {
      index = (index + 1) % PROCESSING_MESSAGES.length;
      setProcessingMessage(PROCESSING_MESSAGES[index]);
    }, 3000);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProcessingProgress((prev) => Math.min(prev + Math.random() * 8, 90));
    }, 500);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, [stage]);

  // Handle file upload
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      setError('Image must be smaller than 25MB');
      return;
    }

    setError(null);
    const previewUrl = URL.createObjectURL(file);
    setUploadedImage({
      file,
      url: previewUrl,
      isSample: false,
    });
    setStage('structure');
  }, []);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    setError(null);
    const previewUrl = URL.createObjectURL(file);
    setUploadedImage({
      file,
      url: previewUrl,
      isSample: false,
    });
    setStage('structure');
  }, []);

  // Handle sample photo selection
  const handleSampleSelect = useCallback((sample: typeof SAMPLE_PHOTOS[0]) => {
    setUploadedImage({
      url: sample.url,
      isSample: true,
    });
    setStage('structure');
  }, []);

  // Handle structure type selection
  const handleStructureSelect = useCallback((typeId: string) => {
    const selection = getStructureSelection(typeId);
    if (selection) {
      setSelectedStructure(selection);
      setStage('style');
    }
  }, []);

  // Handle style selection and start staging
  const handleStyleSelect = useCallback(async (styleSlug: string) => {
    if (!uploadedImage || !user || !selectedStructure) return;

    setSelectedStyle(styleSlug);
    setStage('processing');
    setStagingInProgress(true);
    setError(null);
    setProcessingProgress(0);

    const startTime = Date.now();

    try {
      let imageUrl = uploadedImage.url;

      // Create project for both user uploads AND sample photos
      setProcessingMessage(uploadedImage.file ? 'Uploading your photo' : 'Preparing sample photo');

      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({
          user_id: user.id,
          title: 'My First Staging',
          status: 'in_progress',
        })
        .select()
        .single();

      if (projectError) throw projectError;

      // Upload image to R2 (both user uploads AND sample photos)
      // Use new path format: {userId}/{projectId}/original/{filename}
      let originalFilename: string;
      let storagePath: string;

      if (uploadedImage.file) {
        // User upload - use the file directly
        originalFilename = uploadedImage.file.name;
        storagePath = generateOriginalImagePath(user.id, project.id, originalFilename);

        const uploadResult = await uploadToR2(
          'staging-images',
          storagePath,
          uploadedImage.file,
          uploadedImage.file.type
        );

        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Failed to upload image');
        }

        const signedUrl = await getR2SignedUrl('staging-images', storagePath);
        if (!signedUrl) {
          throw new Error('Failed to get signed URL');
        }
        imageUrl = signedUrl;
      } else {
        // Sample photo - fetch and upload to R2 so the worker can access it
        setProcessingMessage('Preparing sample photo');

        const response = await fetch(uploadedImage.url);
        if (!response.ok) {
          throw new Error('Failed to fetch sample photo');
        }
        const blob = await response.blob();

        // Extract filename from URL for sample photos
        originalFilename = uploadedImage.url.split('/').pop() || 'sample-photo.jpg';
        storagePath = generateOriginalImagePath(user.id, project.id, originalFilename);

        const uploadResult = await uploadToR2(
          'staging-images',
          storagePath,
          blob,
          blob.type || 'image/jpeg'
        );

        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Failed to upload sample photo');
        }

        const signedUrl = await getR2SignedUrl('staging-images', storagePath);
        if (!signedUrl) {
          throw new Error('Failed to get signed URL');
        }
        imageUrl = signedUrl;
      }

      // Create image record (new simplified schema - only original upload info)
      const { data: imageRecord, error: imageError } = await supabase
        .from('images')
        .insert({
          project_id: project.id,
          user_id: user.id,
          original_url: storagePath, // Store the R2 path, not the signed URL
          filename: originalFilename,
        })
        .select()
        .single();

      if (imageError) throw imageError;

      setProcessingMessage(PROCESSING_MESSAGES[0]);

      // Call real staging API for both user uploads AND sample photos
      const stagedResult = await stageImage({
        imageId: imageRecord.id,
        originalUrl: imageUrl,
        style: styleSlug,
        structureSelection: selectedStructure,
      });

      // Update project status to completed
      await supabase
        .from('projects')
        .update({
          status: 'completed',
          updated_at: new Date().toISOString(),
        })
        .eq('id', project.id);

      const endTime = Date.now();
      const processingTimeMs = endTime - startTime;

      setProcessingProgress(100);

      setStagingResult({
        originalUrl: imageUrl,
        stagedUrl: stagedResult.stagedUrl,
        style: styleSlug,
        roomType: selectedStructure.typeId,
        processingTimeMs,
        imageId: imageRecord.id,
        projectId: project.id,
      });

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'onboarding_staging_completed',
          duration_seconds: Math.round(processingTimeMs / 1000),
          style: styleSlug,
          room_type: selectedStructure.typeId,
          structure_domain: selectedStructure.domain,
          structure_category: selectedStructure.categoryId,
          used_sample: uploadedImage.isSample,
          session_id: onboardingData.session_id,
        });
      }

      await onStepComplete('stage', {
        first_photo_uploaded_at: new Date().toISOString(),
        used_sample_photo: uploadedImage.isSample,
        first_style_selected: styleSlug,
        first_room_type_staged: selectedStructure.typeId,
      });
    } catch (err) {
      console.error('Staging error:', err);
      setError(getUserFriendlyStagingError(err));
      setStage('style');
      setStagingInProgress(false);
    }
  }, [uploadedImage, user, selectedStructure, onStepComplete, onboardingData, setStagingResult, setStagingInProgress]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleReset = () => {
    setUploadedImage(null);
    setSelectedStructure(null);
    setSelectedStyle(null);
    setStage('upload');
    setError(null);
  };

  const handleBackToStructure = () => {
    setSelectedStructure(null);
    setStage('structure');
  };

  return (
    <OnboardingLayout
      showProgress={true}
      showBack={stage === 'structure' || stage === 'style'}
      onBack={stage === 'style' ? handleBackToStructure : handleReset}
      showSkip={false}
    >
      <OnboardingStepContainer maxWidth="3xl" centered={stage === 'processing'}>
        <AnimatePresence mode="wait">
          {/* Upload Stage */}
          {stage === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <OnboardingHeading
                eyebrow="The magic moment"
                title="Stage your first room"
                subtitle="Upload a photo or try one of our sample spaces"
              />

              {/* Upload area */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                onClick={handleUploadClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                  'relative group cursor-pointer rounded-3xl p-12 sm:p-16 text-center transition-all duration-300',
                  'border-2 border-dashed',
                  isDragging
                    ? 'border-olive bg-olive/5 scale-[1.02]'
                    : 'border-stone/40 hover:border-olive/50 hover:bg-white/50'
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />

                <div className="size-16 rounded-2xl bg-stone/20 flex items-center justify-center mx-auto mb-6 group-hover:bg-olive/10 transition-colors duration-300">
                  <Upload className="size-7 text-sand group-hover:text-olive transition-colors duration-300" />
                </div>

                <p className="text-charcoal font-sans font-medium text-lg mb-2">
                  {isDragging ? 'Drop your image here' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sand text-sm font-sans">
                  JPG, PNG up to 25MB
                </p>
              </motion.div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-terracotta/10 border border-terracotta/20 rounded-2xl text-terracotta text-sm text-center"
                >
                  {error}
                </motion.div>
              )}

              {/* Sample photos */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-10"
              >
                <p className="text-center text-sand text-sm font-sans mb-5">
                  Or try with a sample
                </p>
                <div className="flex justify-center gap-4 flex-wrap">
                  {SAMPLE_PHOTOS.map((sample, index) => (
                    <motion.button
                      key={sample.url}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 + index * 0.08, duration: 0.4 }}
                      onClick={() => handleSampleSelect(sample)}
                      className="group relative w-28 sm:w-32 aspect-[4/3] rounded-2xl overflow-hidden border-2 border-transparent hover:border-olive/50 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-olive"
                    >
                      <img
                        src={sample.url}
                        alt={sample.label}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/40 transition-colors duration-300 flex items-center justify-center">
                        <span className="text-white text-xs font-sans font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {sample.label}
                        </span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Structure Selection Stage */}
          {stage === 'structure' && uploadedImage && (
            <motion.div
              key="structure"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <OnboardingHeading
                eyebrow="Tell us about your space"
                title="What type of room is this?"
                subtitle="Select the space type for optimal staging"
              />

              {/* Preview of uploaded image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="relative mb-10 max-w-md mx-auto"
              >
                <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-soft">
                  <img
                    src={uploadedImage.url}
                    alt="Your room"
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={handleReset}
                  className="absolute -top-2 -right-2 size-10 bg-charcoal text-white rounded-full flex items-center justify-center hover:bg-charcoal/80 transition-colors shadow-soft"
                  aria-label="Remove image"
                >
                  <X className="size-5" />
                </button>
              </motion.div>

              {/* Structure type options */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="grid grid-cols-2 sm:grid-cols-3 gap-4"
              >
                {STRUCTURE_OPTIONS.map((option, index) => {
                  const Icon = option.icon;
                  return (
                    <motion.button
                      key={option.typeId}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 + index * 0.05, duration: 0.4 }}
                      onClick={() => handleStructureSelect(option.typeId)}
                      whileHover={{ y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      className={cn(
                        'group relative p-6 rounded-2xl border-2 transition-all duration-300',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
                        'border-stone/30 hover:border-olive/50 hover:bg-white/60'
                      )}
                    >
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-12 rounded-xl bg-stone/20 flex items-center justify-center group-hover:bg-olive/10 transition-colors duration-300">
                          <Icon className="size-6 text-sand group-hover:text-olive transition-colors duration-300" />
                        </div>
                        <span className="font-sans font-medium text-charcoal text-sm text-center">
                          {option.label}
                        </span>
                      </div>
                    </motion.button>
                  );
                })}
              </motion.div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-terracotta/10 border border-terracotta/20 rounded-2xl text-terracotta text-sm text-center"
                >
                  {error}
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Style Selection Stage */}
          {stage === 'style' && uploadedImage && selectedStructure && (
            <motion.div
              key="style"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <OnboardingHeading
                eyebrow="Select a style"
                title="Choose your aesthetic"
                subtitle="Pick a design style that matches your vision"
              />

              {/* Preview of uploaded image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="relative mb-10 max-w-lg mx-auto"
              >
                <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-soft">
                  <img
                    src={uploadedImage.url}
                    alt="Your room"
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={handleReset}
                  className="absolute -top-2 -right-2 size-10 bg-charcoal text-white rounded-full flex items-center justify-center hover:bg-charcoal/80 transition-colors shadow-soft"
                  aria-label="Remove image"
                >
                  <X className="size-5" />
                </button>
              </motion.div>

              {/* Style options */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="grid grid-cols-2 sm:grid-cols-3 gap-4"
              >
                {STYLE_OPTIONS.map((style, index) => (
                  <motion.button
                    key={style.slug}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 + index * 0.05, duration: 0.4 }}
                    onClick={() => handleStyleSelect(style.slug)}
                    whileHover={{ y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    className={cn(
                      'group relative rounded-2xl overflow-hidden border-2 transition-all duration-300',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
                      selectedStyle === style.slug
                        ? 'border-olive shadow-soft'
                        : 'border-stone/30 hover:border-olive/50'
                    )}
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={style.preview}
                        alt={style.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-colors duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-white/90 backdrop-blur-sm">
                      <span className="font-sans font-medium text-charcoal text-sm">
                        {style.name}
                      </span>
                    </div>
                    {selectedStyle === style.slug && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 size-6 bg-olive rounded-full flex items-center justify-center"
                      >
                        <Check className="size-4 text-white" />
                      </motion.div>
                    )}
                  </motion.button>
                ))}
              </motion.div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-terracotta/10 border border-terracotta/20 rounded-2xl text-terracotta text-sm text-center"
                >
                  {error}
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Processing Stage */}
          {stage === 'processing' && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-12"
            >
              {/* Image preview with animation overlay */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="relative w-72 sm:w-80 aspect-[4/3] mx-auto mb-10 rounded-3xl overflow-hidden shadow-soft"
              >
                {uploadedImage && (
                  <img
                    src={uploadedImage.url}
                    alt="Processing"
                    className="w-full h-full object-cover"
                  />
                )}
                {/* Scanning effect */}
                <motion.div
                  className="absolute inset-x-0 h-1 bg-olive/60"
                  initial={{ top: 0 }}
                  animate={{ top: ['0%', '100%', '0%'] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-charcoal/10" />
              </motion.div>

              {/* Progress indicator */}
              <div className="max-w-xs mx-auto mb-8">
                <div className="h-1 bg-stone/30 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-olive rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${processingProgress}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <span className="micro-copy">{Math.round(processingProgress)}%</span>
                  <span className="micro-copy">Staging in progress</span>
                </div>
              </div>

              {/* Rotating message */}
              <AnimatePresence mode="wait">
                <motion.p
                  key={processingMessage}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="text-charcoal font-sans text-xl font-medium mb-2"
                >
                  {processingMessage}
                </motion.p>
              </AnimatePresence>

              <p className="text-sand text-sm font-sans">
                This usually takes about 30 seconds
              </p>

              {/* Fun fact */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="mt-10 p-5 bg-white/60 rounded-2xl max-w-sm mx-auto border border-stone/20"
              >
                <p className="text-sand text-sm font-sans">
                  <span className="text-olive font-medium">Did you know?</span>
                  <br />
                  Staged homes sell 73% faster and for 6-10% more than non-staged homes.
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </OnboardingStepContainer>
    </OnboardingLayout>
  );
}
